import { useState, FormEvent } from 'react';
import RelatedPages from '@/components/base/RelatedPages';

const FORM_URL = 'https://readdy.ai/api/form/d7kdndl0lfokr4m6je10';

const productTypes = [
  '膠囊（硬膠囊）',
  '軟膠囊',
  '錠劑',
  '咀嚼錠',
  '粉末包／顆粒',
  '機能飲品',
  '口服液',
  '軟糖',
  '果凍條',
  '茶包',
  '其他劑型',
];

const quantities = [
  '1,000 – 3,000 件',
  '3,000 – 10,000 件',
  '10,000 – 50,000 件',
  '50,000 件以上',
  '尚未確定',
];

const formulaOptions = [
  '有明確配方想法',
  '有概念但需要協助',
  '尚無想法，請建議',
];

const processSteps = [
  {
    step: '01',
    title: '送出需求',
    desc: '您填寫產品方向、預計數量、劑型需求與聯絡資訊。',
    icon: 'ri-file-edit-line',
  },
  {
    step: '02',
    title: '專人初步回覆',
    desc: '康永生技將會盡快與您聯繫，確認基本需求與合作方向。',
    icon: 'ri-customer-service-2-line',
  },
  {
    step: '03',
    title: '需求確認與可行性評估',
    desc: '依照您的品牌定位、通路、預算與產品目標，協助評估適合的配方、劑型與包材方向。',
    icon: 'ri-search-eye-line',
  },
  {
    step: '04',
    title: '初步報價與合作建議',
    desc: '確認需求後，提供初步合作建議、時程方向與報價評估，協助您進入下一步開發流程。',
    icon: 'ri-price-tag-3-line',
  },
];

export default function ContactMain() {
  const [submitState, setSubmitState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="requirement"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;
    setSubmitState('loading');
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitState('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitState('error');
      }
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <>
      {/* Notice bar */}
      <div className="bg-[#e8f5e9] border-y border-[#c8e6c9]">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-3 flex flex-wrap items-center gap-4">
          {[
            { icon: 'ri-time-line', text: '一個工作天內回覆' },
            { icon: 'ri-shield-check-line', text: '個資安全保護' },
            { icon: 'ri-customer-service-2-line', text: '專人服務' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-1.5 text-[#388E3C] text-[0.8125rem] font-medium">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-sm`} />
              </div>
              {item.text}
            </div>
          ))}
        </div>
      </div>

      {/* Process Steps Section */}
      <section className="py-14 bg-[#f7faf7] border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="mb-8">
            <h2 className="text-[#1a2e1a] font-bold text-[1.375rem] md:text-[1.625rem] mb-2">
              送出表單後，接下來會怎麼進行？
            </h2>
            <p className="text-gray-500 text-[0.9rem] leading-[1.75]">
              從填寫到正式開發，我們為您安排清晰的合作流程。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {processSteps.map((s, idx) => (
              <div key={s.step} className="relative bg-white rounded-xl p-5 border border-gray-100">
                {idx < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-[2.25rem] left-full w-5 h-0.5 bg-[#c8e6c9] z-10" />
                )}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#e8f5e9] rounded-full flex-shrink-0 border border-[#c8e6c9]">
                    <i className={`${s.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <span className="text-[#4CAF50] font-bold text-[0.75rem] tracking-widest">STEP {s.step}</span>
                </div>
                <h3 className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-2">{s.title}</h3>
                <p className="text-gray-500 text-[0.8125rem] leading-[1.75]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section className="py-16 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 xl:gap-16 items-start">

            {/* Left: sidebar info */}
            <div className="w-full lg:w-[300px] xl:w-[340px] flex-shrink-0 space-y-5">
              <div>
                <h2 className="text-[#1a2e1a] font-bold text-[1.25rem] mb-1.5">填寫前請參考</h2>
                <p className="text-gray-500 text-[0.875rem] leading-[1.75]">
                  以下資訊可協助您更快速完成表單填寫，提升合作溝通效率。
                </p>
              </div>

              {[
                { icon: 'ri-flask-line', title: '產品開發諮詢', desc: '無論是全新配方開發、劑型選擇，或是修改現有配方，都歡迎與我們討論。' },
                { icon: 'ri-leaf-line', title: '天然原料選材', desc: '我們堅持自然選材，可協助您挑選符合品牌定位的原料組合。' },
                { icon: 'ri-file-shield-2-line', title: '法規與標示確認', desc: '可同步協助確認產品法規風險與標示方向，確保合法上市。' },
                { icon: 'ri-box-3-line', title: '包裝設計服務', desc: '從包材選擇到視覺設計，提供一條龍包裝規劃支援。' },
              ].map((tip) => (
                <div key={tip.title} className="flex items-start gap-3 bg-[#f7faf7] rounded-xl p-4 border border-gray-100">
                  <div className="w-9 h-9 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0 border border-green-100">
                    <i className={`${tip.icon} text-[#4CAF50] text-base`} />
                  </div>
                  <div>
                    <p className="text-[#1a2e1a] font-semibold text-[0.875rem] mb-0.5">{tip.title}</p>
                    <p className="text-gray-500 text-[0.8125rem] leading-[1.7]">{tip.desc}</p>
                  </div>
                </div>
              ))}

              {/* Direct contact */}
              <div className="bg-[#f7faf7] rounded-xl p-5 border border-gray-100">
                <p className="text-[#1a2e1a] font-bold text-[0.9375rem] mb-3">也可直接聯絡我們</p>
                <div className="space-y-2.5">
                  <a href="tel:04-22029858" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem]">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200">
                      <i className="ri-phone-line text-[#4CAF50] text-sm" />
                    </div>
                    (04)-22029858
                  </a>
                  <a href="mailto:natural1070606@gmail.com" className="flex items-center gap-2.5 text-gray-600 hover:text-[#4CAF50] transition-colors cursor-pointer text-[0.875rem] break-all">
                    <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg border border-gray-200 flex-shrink-0">
                      <i className="ri-mail-line text-[#4CAF50] text-sm" />
                    </div>
                    natural1070606@gmail.com
                  </a>
                </div>
              </div>

              {/* LINE sidebar */}
              <div className="bg-[#e8f5e9] border border-[#c8e6c9] rounded-xl p-5">
                <div className="flex items-center gap-2 mb-1.5">
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className="ri-line-fill text-[#4CAF50] text-xl" />
                  </div>
                  <p className="text-[#1a2e1a] font-bold text-[0.9375rem]">加入 LINE 洽談</p>
                </div>
                <p className="text-gray-600 text-[0.8125rem] leading-[1.7] mb-3">
                  更即時的溝通方式，加入 LINE 官方帳號與我們直接洽談。
                </p>
                <a
                  href="https://lin.ee/X7Hcpf4"
                  rel="nofollow"
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-semibold text-[0.875rem] px-4 py-2.5 rounded-full transition-all duration-300 cursor-pointer w-full"
                >
                  <i className="ri-line-fill" />
                  立即加入 LINE
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="w-full lg:flex-1">
              <h2 className="text-[#1a2e1a] font-bold text-[1.625rem] mb-2">填寫諮詢表單</h2>
              <p className="text-gray-500 text-[0.9375rem] mb-7 leading-[1.75]">
                請填寫以下欄位，我們將依您的需求安排專人回覆。
              </p>

              {submitState === 'success' ? (
                <div className="flex flex-col items-center text-center bg-[#f7faf7] rounded-2xl border border-green-100 overflow-hidden">
                  <div className="w-full px-8 pt-12 pb-8">
                    <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5 border border-green-200 mx-auto">
                      <i className="ri-checkbox-circle-fill text-[#4CAF50] text-3xl" />
                    </div>
                    <h3 className="text-[#1a2e1a] font-bold text-[1.375rem] mb-3">需求已送出，感謝您的洽詢！</h3>
                    <p className="text-gray-500 text-[0.9375rem] leading-[1.75] max-w-sm mx-auto">
                      康永生技將於 1 個工作天內與您聯繫，協助確認產品方向、劑型需求、預計數量與合作方式。
                    </p>
                  </div>

                  <div className="w-full bg-[#e8f5e9] border-t border-[#c8e6c9] px-6 sm:px-8 py-6">
                    <p className="text-[#1a2e1a] font-semibold text-[0.9375rem] mb-1">若您希望更快取得回覆</p>
                    <p className="text-gray-600 text-[0.875rem] leading-[1.75] mb-4">
                      也可以立即加入 LINE，由專人協助您確認需求。
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <a
                        href="https://lin.ee/X7Hcpf4"
                        rel="nofollow"
                        className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] text-white font-bold text-[1rem] px-7 py-3.5 rounded-full transition-all duration-300 cursor-pointer w-full sm:w-auto"
                      >
                        <i className="ri-line-fill text-lg" />
                        立即加入 LINE
                      </a>
                      <span className="text-[#4CAF50] text-[0.875rem] font-medium">LINE@ ID：@126uwznp</span>
                    </div>
                  </div>

                  <div className="px-8 py-5">
                    <button
                      onClick={() => setSubmitState('idle')}
                      className="whitespace-nowrap border border-gray-300 text-gray-500 hover:border-[#4CAF50] hover:text-[#4CAF50] font-medium text-[0.875rem] px-6 py-2.5 rounded-full transition-all duration-300 cursor-pointer"
                    >
                      再次填寫
                    </button>
                  </div>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        姓名 <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="contact_name"
                        type="text"
                        required
                        placeholder="請輸入您的姓名"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        公司／品牌名稱
                      </label>
                      <input
                        name="company_name"
                        type="text"
                        placeholder="請輸入公司或品牌名稱"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        聯絡電話 <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="請輸入聯絡電話"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        name="email"
                        type="email"
                        required
                        placeholder="請輸入電子信箱"
                        className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      想開發的產品類型
                    </label>
                    <select
                      name="product_type"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>請選擇產品類型</option>
                      {productTypes.map((p) => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      預計製作數量
                    </label>
                    <select
                      name="quantity"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] focus:outline-none focus:border-[#4CAF50] transition-colors bg-white cursor-pointer"
                      defaultValue=""
                    >
                      <option value="" disabled>請選擇預計數量</option>
                      {quantities.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-2">
                      是否已有配方想法
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {formulaOptions.map((opt) => (
                        <label key={opt} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name="formula_idea"
                            value={opt}
                            className="accent-[#4CAF50] cursor-pointer"
                          />
                          <span className="text-[#1a2e1a] text-[0.875rem]">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[#1a2e1a] font-medium text-[0.875rem] mb-1.5">
                      需求說明{' '}
                      <span className={`text-[0.75rem] font-normal ${charCount > 480 ? 'text-red-500' : 'text-gray-400'}`}>
                        ({charCount}/500)
                      </span>
                    </label>
                    <textarea
                      name="requirement"
                      rows={5}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      placeholder="請簡述您的需求，例如：產品方向、目標族群、預算規劃、合作方式，或其他補充說明。"
                      className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-[#1a2e1a] placeholder-gray-400 focus:outline-none focus:border-[#4CAF50] transition-colors resize-none bg-white"
                    />
                    {charCount > 500 && (
                      <p className="text-red-500 text-[0.75rem] mt-1">需求說明不能超過 500 字</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitState === 'loading' || charCount > 500}
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-[#4CAF50] hover:bg-[#43a047] disabled:opacity-60 text-white font-bold text-[1rem] py-4 rounded-lg transition-all duration-300 cursor-pointer"
                  >
                    {submitState === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg" />
                        送出中…
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line text-lg" />
                        送出諮詢
                      </>
                    )}
                  </button>

                  {submitState === 'error' && (
                    <p className="text-red-500 text-[0.875rem] text-center">送出失敗，請稍後再試或直接致電我們。</p>
                  )}

                  <p className="text-gray-400 text-[0.8125rem] text-center leading-[1.75]">
                    送出後將由專人與您聯繫，協助了解產品方向、劑型需求與合作流程。
                  </p>

                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <RelatedPages items={[
        { title: '常見問題', desc: '品牌、起訂量、法規、通路…常見答疑全整理，快速找到答案。', href: '/cooperation/faq' },
        { title: '合作流程', desc: '從需求對焦到交貨，清楚了解與康永合作的每個步驟。', href: '/cooperation/process' },
        { title: 'LINE 諮詢', desc: '直接加入 LINE 快速洽談，專人即時回覆。', href: 'https://lin.ee/X7Hcpf4', external: true },
      ]} />
    </>
  );
}